import { Search } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { formatBRL, pixAmount, searchCatalog } from "@/lib/catalog";
import { optimizeImage } from "@/lib/images";
import { HUB_PATHS, yberaSearchUrl } from "@/lib/site";

const PAGE_HITS = [
  { to: HUB_PATHS.progressiva, label: "Progressiva Fashion Gold" },
  { to: HUB_PATHS.progressivaOficial, label: "Parceiro oficial Fashion Gold" },
  { to: HUB_PATHS.cronograma, label: "Cronograma capilar · Cuidados Profundos" },
  { to: HUB_PATHS.antiqueda, label: "100Tímetros antiqueda" },
  { to: HUB_PATHS.oleoMirra, label: "Óleo de Mirra Ybera" },
  { to: HUB_PATHS.soroVello, label: "Soro Vital Vello Alfa-Lactobaby" },
] as const;

type HeaderSearchProps = {
  id?: string;
  onNavigate?: () => void;
};

export function HeaderSearch({ id, onNavigate }: HeaderSearchProps) {
  const reactId = useId();
  const inputId = id ?? `site-search-${reactId}`;
  const listId = `${inputId}-list`;
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const products = useMemo(() => searchCatalog(query), [query]);
  const pages = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (term.length < 2) return [];
    return PAGE_HITS.filter((page) => page.label.toLowerCase().includes(term)).slice(0, 4);
  }, [query]);
  const hasResults = products.length > 0 || pages.length > 0;
  const showPanel = open && query.trim().length >= 2;

  useEffect(() => {
    setOpen(false);
    setQuery("");
  }, [pathname]);

  useEffect(() => {
    function onPointer(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, []);

  function goYbera() {
    const href = yberaSearchUrl(query);
    window.open(href, "_blank", "noopener,noreferrer");
    setOpen(false);
    onNavigate?.();
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (pages[0]) {
      navigate(pages[0].to);
      onNavigate?.();
      return;
    }
    if (products[0]) {
      navigate(products[0].path);
      onNavigate?.();
      return;
    }
    if (query.trim().length >= 2) goYbera();
  }

  return (
    <form ref={rootRef} className="relative w-full" onSubmit={onSubmit} role="search">
      <label htmlFor={inputId} className="sr-only">
        Buscar produtos
      </label>
      <input
        id={inputId}
        type="search"
        value={query}
        autoComplete="off"
        placeholder="Procurando por algo?"
        className="h-11 w-full rounded-full border border-neutral-300 bg-white py-2 pl-4 pr-12 text-sm text-neutral-900 outline-none placeholder:text-neutral-500 focus:border-neutral-800"
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      <button
        type="submit"
        className="absolute right-1.5 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-neutral-800"
        aria-label="Buscar"
      >
        <Search className="h-4 w-4" aria-hidden />
      </button>

      {showPanel ? (
        <div
          id={listId}
          className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 overflow-hidden rounded-md border border-neutral-200 bg-white shadow-lg"
        >
          {pages.length ? (
            <ul className="border-b border-neutral-100 py-1">
              {pages.map((page) => (
                <li key={page.to}>
                  <Link
                    to={page.to}
                    className="block px-4 py-2 text-sm text-neutral-800 hover:bg-neutral-50"
                    onClick={() => {
                      setOpen(false);
                      onNavigate?.();
                    }}
                  >
                    {page.label}
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
          {products.length ? (
            <ul className="max-h-80 overflow-y-auto py-1">
              {products.map((product) => (
                <li key={product.slug}>
                  <Link
                    to={product.path}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-neutral-50"
                    onClick={() => {
                      setOpen(false);
                      onNavigate?.();
                    }}
                  >
                    {product.image ? (
                      <img
                        src={optimizeImage(product.image, 80)}
                        alt=""
                        width={40}
                        height={40}
                        className="h-10 w-10 shrink-0 rounded object-contain bg-neutral-50"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : null}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-neutral-900">{product.name}</span>
                      {product.price != null ? (
                        <span className="text-xs text-neutral-500">{formatBRL(pixAmount(product.price))} no PIX</span>
                      ) : null}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
          {!hasResults ? (
            <p className="px-4 py-3 text-sm text-neutral-500">Nenhum produto encontrado.</p>
          ) : null}
          <a
            href={yberaSearchUrl(query)}
            rel="sponsored noopener noreferrer"
            target="_blank"
            className="block border-t border-neutral-100 px-4 py-2.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
            onClick={() => {
              setOpen(false);
              onNavigate?.();
            }}
          >
            Buscar “{query.trim()}” na Ybera
          </a>
        </div>
      ) : null}
    </form>
  );
}
